import React from 'react';
import { X, Calendar, ChevronDown } from 'lucide-react';
import type { Box, Department, Resource } from './types';
import { useT } from './i18n/index';

// ── Props ─────────────────────────────────────────────────────────────────────

// Computes the status button's icon/colors from box.status. Used to be computed by
// the caller and passed in as a `statusIcon` prop — but that always returns a new
// object literal, and since box.status is already covered by comparing `box` itself
// (see CanvasBoxProps), an unstable derived-object prop like that defeated
// React.memo below for every single box on every render, regardless of the
// onAction stabilization. Computing it in here instead means its freshness never
// crosses the memo boundary.
function getStatusIcon(status: string): { symbol: string; color: string; bg: string } {
  // Solid, saturated red (not the muted red-100/red-600 pairing) — this is the box's
  // one true "issue" indicator and needs to read as urgent at a glance, same intent
  // as the 24/7/24/5 badges' own solid-color convention. Plain "!" rather than the
  // "❗" emoji — that glyph renders with its own baked-in red fill on every platform,
  // ignoring `color` entirely, which made it unreadable (red-on-red) against this bg.
  if (status === 'blocked') return { symbol: '!', color: 'text-black', bg: 'bg-red-600' };
  if (status === 'on-hold') return { symbol: '⏳', color: 'text-yellow-600', bg: 'bg-yellow-100' };
  return { symbol: '', color: 'text-gray-400', bg: 'bg-gray-100' };
}

// Per-action argument tuples. Indexing this by action name (via CanvasBoxAction,
// below) is what lets `onAction` type-check every one of its ~23 call sites
// against the SPECIFIC args that action takes — e.g. 'titleChange' must get a
// string, 'resizeMove' two numbers — instead of the `...args: any[]` this
// replaces, with zero change to how any call site is actually written.
export type CanvasBoxActionArgs = {
  pointerDown: [e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>];
  contextMenu: [e: React.MouseEvent<HTMLDivElement>];
  doubleClick: [e: React.MouseEvent<HTMLDivElement>];
  mouseEnter: [];
  mouseLeave: [];
  titleChange: [value: string];
  titleFocus: [];
  titleBlur: [];
  durationChange: [value: string];
  durationFocus: [];
  durationBlur: [];
  assigneeChange: [value: string];
  assigneeFocus: [];
  assigneeBlur: [];
  delete: [];
  colorChange: [color: string];
  statusClick: [];
  completionClick: [];
  finishBadgeClick: [];
  startBadgeClick: [];
  subitemBadgeClick: [];
  linkClick: [];
  resizeMove: [width: number, height: number];
  connectHandle: [side: 'left' | 'right' | 'top' | 'bottom'];
};

export type CanvasBoxAction = keyof CanvasBoxActionArgs;

export interface CanvasBoxProps {
  box: Box;
  isSelected: boolean;
  isDragging: boolean;
  isEditingTitle: boolean;
  editingDuration: { id: number; value: string } | null; // already filtered to this box or null
  // Replaces the old simple/advanced mode: a box's duration/assignee fields (and the
  // other advanced-only chrome below — status/completion buttons, category picker,
  // always-shown start/finish badges) are editable/visible whenever THIS box is
  // selected, OR unconditionally for every box when viewAllFields is on (the old
  // "advanced" mode's exact effect, now a persisted View-menu toggle instead of a
  // whole-canvas mode switch).
  viewAllFields: boolean;
  departments: Department[];
  resources: Resource[];
  isHovered: boolean;
  zoom: number;
  showConnectHandles: boolean;
  isCritical: boolean;
  isConflict: boolean;
  isConnectSource: boolean;
  endText: string;
  startText: string;
  showStartDates: boolean;
  showFinishDates: boolean;
  // Number of subitems nested under this box (see Box.parentBoxId) — renders a badge
  // when > 0. Passed as a plain number (not the whole boxes array) so a change to an
  // unrelated box's subitem count doesn't defeat this component's React.memo.
  subitemCount: number;
  // Canvas-wide "Show" display toggles (CanvasTopbar's dropdown) — gate the two
  // badges below independently of whether the underlying data exists.
  showSubitemBadge: boolean;
  showUidBadge: boolean;

  // Single stable dispatcher instead of ~23 separate handler props. FlowchartBuilder
  // re-renders this component's parent on every pan/select frame; if each handler
  // were a fresh inline closure (as they used to be), that alone would defeat
  // React.memo below for every box on every one of those renders, regardless of
  // whether anything about THIS box actually changed. `box` is passed back on every
  // call so the dispatcher always knows which box to act on.
  onAction: <A extends CanvasBoxAction>(action: A, box: Box, ...args: CanvasBoxActionArgs[A]) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

const CanvasBox = React.memo(function CanvasBox(props: CanvasBoxProps) {
  const {
    box,
    isSelected, isDragging, isEditingTitle, editingDuration,
    viewAllFields, departments, resources, isHovered, zoom,
    showConnectHandles, isCritical, isConflict, isConnectSource,
    endText, onAction,
    startText, showStartDates, showFinishDates, subitemCount, showSubitemBadge, showUidBadge,
  } = props;

  const { t } = useT();
  // "Edit this box like the old advanced mode" — true once either this specific box
  // is selected, or viewAllFields is on for the whole canvas. See viewAllFields's own
  // prop comment above for the full rationale.
  const editableHere = viewAllFields || isSelected;
  const statusIcon = getStatusIcon(box.status);
  const [showResourceDropdown, setShowResourceDropdown] = React.useState(false);
  const resourceDropdownRef = React.useRef<HTMLDivElement | null>(null);
  const assignedNames = (box.assignee || '').split(',').map(n => n.trim()).filter(Boolean);
  const toggleResource = (name: string) => {
    const next = assignedNames.includes(name)
      ? assignedNames.filter(n => n !== name)
      : [...assignedNames, name];
    onAction('assigneeChange', box, next.join(', '));
  };
  // Group by team so the dropdown is organized per team; no header if there's only 1 group.
  const resourceGroups = React.useMemo(() => {
    const groups = new Map<string, Resource[]>();
    resources.forEach(r => {
      const key = r.team || '';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(r);
    });
    return Array.from(groups.entries());
  }, [resources]);

  // Close the dropdown on a click elsewhere. A "position: fixed" backdrop doesn't work
  // here: this box sits inside the pan/zoom container which has a CSS transform, which
  // makes "fixed" position relative to that transformed ancestor instead of the viewport,
  // so it wouldn't cover the whole canvas. A document-level listener doesn't have that problem.
  React.useEffect(() => {
    if (!showResourceDropdown) return;
    const handleDocMouseDown = (e: MouseEvent) => {
      if (resourceDropdownRef.current && !resourceDropdownRef.current.contains(e.target as Node)) {
        setShowResourceDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleDocMouseDown);
    return () => document.removeEventListener('mousedown', handleDocMouseDown);
  }, [showResourceDropdown]);

  const linkIcon = (
    <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
    </svg>
  );

  return (
    <div
      style={{
        left: box.x,
        top: box.y,
        width: box.width,
        height: box.height,
        zIndex: showResourceDropdown ? 11 : isDragging ? 10 : isSelected ? 8 : 2,
        touchAction: 'none',
        backgroundColor: box.type === 'regular' ? (box.departmentColor || '#ffffff') : undefined,
        outline: isCritical ? '3px solid #ef4444' : undefined,
        outlineOffset: 1,
        boxShadow: isConnectSource
          ? '0 0 0 4px rgba(16, 185, 129, 0.6)'
          : isSelected
          ? '0 0 0 3px rgba(59, 130, 246, 0.5)'
          : undefined,
      }}
      className={`absolute rounded-lg shadow-lg cursor-move ${
        box.type === 'callout'
          ? 'bg-white border-2 border-dashed border-yellow-500'
          : box.type === 'startday'
          ? 'bg-blue-50 border-2 border-blue-500 rounded-full'
          : 'border-2 border-blue-500'
      }`}
      data-box-el="true"
      onMouseDown={(e) => onAction('pointerDown', box, e)}
      onTouchStart={(e) => onAction('pointerDown', box, e)}
      onContextMenu={(e) => onAction('contextMenu', box, e)}
      onDoubleClick={(e) => onAction('doubleClick', box, e)}
      onMouseEnter={() => onAction('mouseEnter', box)}
      onMouseLeave={() => onAction('mouseLeave', box)}
    >
      <div className="h-full flex flex-col p-3 relative pointer-events-none">
        {box.type === 'startday' ? (
          <div className="h-full flex items-center justify-center">
            <input
              type="text"
              value={box.title}
              onChange={(e) => onAction('titleChange', box, e.target.value)}
              onFocus={() => onAction('titleFocus', box)}
              // Collapse the field's own internal selection on blur — a blurred
              // input/textarea keeps showing its last text-selection range highlighted
              // (usually in gray) even after it loses focus, and nothing else here ever
              // clears it. Left unfixed, every title you've ever double-clicked to
              // rename (which select-alls the text, see useBoxActions.ts's doubleClick)
              // stays visibly "selected" indefinitely — reported as several box titles
              // suddenly appearing selected with no way to dismiss it via a canvas click.
              onBlur={(e) => { e.currentTarget.setSelectionRange(0, 0); onAction('titleBlur', box); }}
              className="w-full text-center text-base font-bold text-blue-700 bg-transparent border-none outline-none cursor-text pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
              placeholder="0"
              maxLength={3}
            />
          </div>
        ) : box.type === 'callout' ? (
          <>
            <textarea
              value={box.title}
              readOnly={!isEditingTitle}
              onChange={(e) => {
                const newValue = e.target.value;
                const textarea = e.target;
                const cursorPos = textarea.selectionStart;
                const maxHeight = box.height - 24;
                if (textarea.scrollHeight > maxHeight) {
                  // Note is full at its current width — grow wider instead of silently
                  // refusing further keystrokes, up to the same max width the manual
                  // resize handle/modal already allow. Once at that cap, fall back to
                  // the old block-and-restore-cursor behavior.
                  const NOTE_MAX_WIDTH = 800;
                  if (box.width < NOTE_MAX_WIDTH) {
                    // Order matters: onTitleChange (updateTitle) applies its update from a
                    // stale `boxes` snapshot rather than a functional setState, so calling
                    // it AFTER onResizeMove's functional update would silently overwrite the
                    // just-applied width change back to the old value.
                    onAction('titleChange', box, newValue);
                    onAction('resizeMove', box, Math.min(box.width + 40, NOTE_MAX_WIDTH), box.height);
                    return;
                  }
                  const insertedLen = newValue.length - box.title.length;
                  const restoredPos = Math.max(0, cursorPos - insertedLen);
                  requestAnimationFrame(() => {
                    textarea.selectionStart = restoredPos;
                    textarea.selectionEnd = restoredPos;
                  });
                  return;
                }
                onAction('titleChange', box, newValue);
              }}
              onMouseDown={(e) => { if (isEditingTitle) e.stopPropagation(); }}
              onFocus={() => onAction('titleFocus', box)}
              onBlur={(e) => { e.currentTarget.setSelectionRange(0, 0); onAction('titleBlur', box); }}
              className="font-medium text-gray-800 bg-transparent border-none outline-none resize-none w-full h-full"
              placeholder="Add note..."
              spellCheck={false}
              style={{
                overflow: 'hidden',
                padding: 0,
                margin: 0,
                lineHeight: '20px',
                fontSize: '15px',
                fontFamily: '"Cascadia Code", "Consolas", "Monaco", monospace',
                cursor: isEditingTitle ? 'text' : 'inherit',
                pointerEvents: isEditingTitle ? 'auto' : 'none',
                userSelect: isEditingTitle ? 'text' : 'none',
                WebkitUserSelect: isEditingTitle ? 'text' : 'none',
              }}
            />
            {box.url && (
              <button
                style={{ position: 'absolute', top: -9, right: 22, width: 18, height: 18, background: '#dbeafe', border: '1px solid #3b82f6', borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'auto', zIndex: 10 }}
                onClick={e => { e.stopPropagation(); onAction('linkClick', box); }}
                title="Link"
              >
                {linkIcon}
              </button>
            )}
          </>
        ) : (
          // Regular box
          <>
            <div style={{ height: '60px', overflow: 'hidden', position: 'relative' }}>
              <textarea
                value={box.title}
                readOnly={!isEditingTitle}
                onChange={(e) => {
                  const newValue = e.target.value;
                  const textarea = e.target;
                  const cursorPos = textarea.selectionStart;
                  const lines = newValue.split('\n');
                  if (lines.length > 3 || textarea.scrollHeight > 60) {
                    const insertedLen = newValue.length - box.title.length;
                    const restoredPos = Math.max(0, cursorPos - insertedLen);
                    requestAnimationFrame(() => {
                      textarea.selectionStart = restoredPos;
                      textarea.selectionEnd = restoredPos;
                    });
                    return;
                  }
                  onAction('titleChange', box, newValue);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && box.title.split('\n').length >= 3) e.preventDefault();
                }}
                onMouseDown={(e) => { if (isEditingTitle) e.stopPropagation(); }}
                onFocus={() => onAction('titleFocus', box)}
                onBlur={(e) => { e.currentTarget.setSelectionRange(0, 0); onAction('titleBlur', box); }}
                className="font-semibold text-gray-800 bg-transparent border-none outline-none resize-none w-full text-center"
                style={{
                  height: '60px',
                  lineHeight: '20px',
                  padding: 0,
                  margin: 0,
                  overflow: 'hidden',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  fontSize: '16px',
                  fontFamily: '"Cascadia Code", "Consolas", "Monaco", monospace',
                  cursor: isEditingTitle ? 'text' : 'inherit',
                  pointerEvents: isEditingTitle ? 'auto' : 'none',
                  userSelect: isEditingTitle ? 'text' : 'none',
                  WebkitUserSelect: isEditingTitle ? 'text' : 'none',
                }}
                rows={3}
                spellCheck={false}
              />
            </div>

            {/* Duration and Assignee inputs */}
            {(editableHere || box.duration || box.assignee) && (
              <div style={{ position: 'absolute', bottom: 6, left: 12 }} className="flex gap-2 items-center">
                {(editableHere || box.duration) && (
                  <input
                    type="text"
                    inputMode="text"
                    value={editingDuration ? editingDuration.value : (box.duration || '')}
                    readOnly={!editableHere}
                    // Disabled whenever subitems exist — calcSchedule already ignores
                    // box.duration in favor of the subitems' rolled-up duration in that
                    // case (see lib/gantt.ts), so a manual edit here would silently do
                    // nothing for scheduling and just get overwritten by syncParentDuration
                    // on the next subitem change anyway. See title below for the tooltip.
                    disabled={subitemCount > 0}
                    onChange={(e) => {
                      if (!editableHere) return;
                      const v = e.target.value;
                      if (v === '' || /^[\d]*(?:\.\d*)?[hdwHDW]?$/.test(v)) onAction('durationChange', box, v);
                    }}
                    onFocus={() => { if (editableHere) onAction('durationFocus', box); }}
                    onBlur={() => onAction('durationBlur', box)}
                    placeholder="5d"
                    title={subitemCount > 0 ? t('durationDeterminedBySubitems') : undefined}
                    className={`w-12 text-xs px-1 py-1 text-center ${
                      editableHere
                        ? `border border-gray-300 rounded pointer-events-auto ${subitemCount > 0 ? 'bg-gray-200 cursor-not-allowed' : 'bg-white'}`
                        : 'pointer-events-none'
                    }`}
                    style={!editableHere
                      ? { backgroundColor: box.departmentColor || '#dcfce7', border: 'none', outline: 'none' }
                      : {}
                    }
                    onClick={(e) => e.stopPropagation()}
                    maxLength={5}
                  />
                )}
                {(editableHere || box.assignee) && (
                  <div ref={resourceDropdownRef} className="relative" style={{ width: '105px', pointerEvents: editableHere ? 'auto' : 'none' }}>
                    <input
                      type="text"
                      value={box.assignee || ''}
                      readOnly={!editableHere}
                      onChange={(e) => editableHere && onAction('assigneeChange', box, e.target.value)}
                      onFocus={() => { if (editableHere) onAction('assigneeFocus', box); }}
                      onBlur={() => onAction('assigneeBlur', box)}
                      placeholder="Name"
                      className={`text-xs px-2 py-1 w-full ${
                        editableHere
                          ? `border border-gray-300 bg-white rounded pointer-events-auto${resources.length > 0 ? ' pr-5' : ''}`
                          : 'pointer-events-none'
                      }`}
                      style={!editableHere
                        ? { backgroundColor: box.departmentColor || '#dcfce7', border: 'none', outline: 'none' }
                        : {}
                      }
                      onClick={(e) => e.stopPropagation()}
                    />
                    {editableHere && resources.length > 0 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowResourceDropdown(v => !v); }}
                        onMouseDown={(e) => e.preventDefault()}
                        title={t('assignResources')}
                        style={{ position: 'absolute', top: 0, right: 0, height: '100%', width: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280', pointerEvents: 'auto' }}
                      >
                        <ChevronDown size={12} />
                      </button>
                    )}
                    {showResourceDropdown && (
                      <div
                        className="absolute bg-white rounded shadow-md border border-gray-200"
                        style={{ top: '100%', left: 0, marginTop: 2, zIndex: 30, minWidth: '140px', maxHeight: '160px', overflowY: 'auto', pointerEvents: 'auto' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                          {resourceGroups.map(([team, group]) => (
                            <div key={team || '__none__'}>
                              {resourceGroups.length > 1 && (
                                <div className="px-2 text-xs font-bold text-gray-400 uppercase tracking-wide truncate" style={{ paddingTop: 6, paddingBottom: 2 }}>
                                  {team || t('noTeam')}
                                </div>
                              )}
                              {group.map((r, i) => (
                                <label key={i} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 text-xs cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={assignedNames.includes(r.name)}
                                    onChange={() => toggleResource(r.name)}
                                  />
                                  {r.category && (
                                    <span
                                      style={{ width: 10, height: 10, borderRadius: 3, flexShrink: 0, background: departments.find(d => d.name === r.category)?.color || 'transparent' }}
                                    />
                                  )}
                                  <span className="truncate">{r.name}</span>
                                </label>
                              ))}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {box.url && (
              <button
                style={{ position: 'absolute', top: -9, right: 22, width: 18, height: 18, background: '#dbeafe', border: '1px solid #3b82f6', borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'auto', zIndex: 10 }}
                onClick={e => { e.stopPropagation(); onAction('linkClick', box); }}
                title="Link"
              >
                {linkIcon}
              </button>
            )}
          </>
        )}
      </div>

      {/* Delete button on hover */}
      {isHovered && (
        <button
          onClick={(e) => { e.stopPropagation(); onAction('delete', box); }}
          className="absolute text-red-500 hover:text-red-700 border border-red-300 bg-white rounded-full w-5 h-5 flex items-center justify-center shadow-sm pointer-events-auto"
          style={{ top: '-5px', right: '-5px', zIndex: 23 }}
        >
          <X size={14} />
        </button>
      )}

      {/* Color picker on selection (regular boxes) — was also gated on advanced mode,
          but a per-box floating picker only ever makes sense for the one selected
          box anyway, so selection alone is sufficient now. */}
      {isSelected && box.type === 'regular' && (
        <div
          className="absolute top-0 left-0 pointer-events-auto"
          // Pushed an extra 20px left of the box edge — the status icon badge
          // (-top-3 -left-3, 24px wide) straddles x=0, so the previous translate(-100%)
          // alone put this popup's right edge right in the middle of that badge.
          style={{ zIndex: 24, transform: 'translate(calc(-100% - 20px), -10px)' }}
        >
          <div className="bg-white rounded shadow-md p-2">
            <div className="text-xs font-semibold mb-1">Category:</div>
            <div className="flex flex-col gap-1">
              {departments.map((dept, idx) => (
                <button
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); onAction('colorChange', box, dept.color); }}
                  className="flex items-center gap-2 px-2 py-1 hover:bg-gray-100 rounded text-xs"
                >
                  <div className="w-4 h-4 rounded border" style={{ backgroundColor: dept.color }} />
                  <span>{dept.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Resize handle for callout boxes */}
      {box.type === 'callout' && (
        <div
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize resize-handle pointer-events-auto"
          style={{ background: 'linear-gradient(135deg, transparent 50%, #eab308 50%)', borderBottomRightRadius: '0.5rem', zIndex: 22 }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            const startX = e.clientX;
            const startY = e.clientY;
            const sw = box.width;
            const sh = box.height;
            const handleMove = (me: MouseEvent) => {
              const dx = (me.clientX - startX) / zoom;
              const dy = (me.clientY - startY) / zoom;
              onAction('resizeMove', box, Math.max(120, sw + dx), Math.max(80, sh + dy));
            };
            const handleUp = () => {
              document.removeEventListener('mousemove', handleMove);
              document.removeEventListener('mouseup', handleUp);
            };
            document.addEventListener('mousemove', handleMove);
            document.addEventListener('mouseup', handleUp);
          }}
        />
      )}

      {/* Private-comment lock badge (top-left) — box.privateTo is only ever truthy
          here for the user it's private to; everyone else never receives this box
          as a prop at all (see FlowchartBuilder.tsx's visibleBoxes). */}
      {box.type === 'callout' && box.privateTo && (
        <div
          className="absolute flex items-center justify-center pointer-events-none border-2 border-white shadow-md"
          style={{ zIndex: 21, background: '#7c3aed', borderRadius: 4, padding: '1px 4px', top: '-0.75rem', left: '-0.75rem' }}
          title={t('privateCommentBadgeHint')}
        >
          <span style={{ color: 'white', fontSize: 10, lineHeight: 1 }}>🔒</span>
        </div>
      )}

      {/* Status icon (top-left) */}
      {box.type === 'regular' && (editableHere || (box.status && box.status !== 'none')) && (
        <button
          onClick={(e) => { e.stopPropagation(); onAction('statusClick', box); }}
          className={`absolute -top-3 -left-3 w-6 h-6 rounded flex items-center justify-center text-base font-bold pointer-events-auto ${statusIcon.bg} ${statusIcon.color} border-2 border-white shadow-md`}
          style={{ zIndex: 21 }}
        >
          {statusIcon.symbol}
        </button>
      )}

      {/* 24/7 badge (top-right) */}
      {box.type === 'regular' && box.alwaysOn && (
        <div
          className="absolute flex items-center justify-center pointer-events-none border-2 border-white shadow-md"
          style={{ zIndex: 21, background: '#7c3aed', borderRadius: 4, padding: '1px 3px', top: '-0.75rem', right: '-0.75rem' }}
          title="24/7 execution"
        >
          <span style={{ color: 'white', fontSize: 8, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.3px' }}>24/7</span>
        </div>
      )}

      {/* 24/5 badge (top-right, left of 24/7 if both active) */}
      {box.type === 'regular' && box.is24_5 && (
        <div
          className="absolute flex items-center justify-center pointer-events-none border-2 border-white shadow-md"
          style={{ zIndex: 21, background: '#0891b2', borderRadius: 4, padding: '1px 3px', top: '-0.75rem', right: box.alwaysOn ? '1.5rem' : '-0.75rem' }}
          title="24/5 execution"
        >
          <span style={{ color: 'white', fontSize: 8, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.3px' }}>24/5</span>
        </div>
      )}

      {/* Subitem count badge (top-right, just below the 24/5/24/7 indicators) —
          clickable, same as the start/finish date badges below: opens the
          Subitems panel directly instead of only being reachable via the
          box's right-click menu's "Edit subitems" item. */}
      {showSubitemBadge && box.type === 'regular' && subitemCount > 0 && (
        <div
          onClick={(e) => { e.stopPropagation(); onAction('subitemBadgeClick', box); }}
          className="absolute flex items-center justify-center pointer-events-auto cursor-pointer border-2 border-white shadow-md"
          style={{ zIndex: 21, background: '#4b5563', borderRadius: 4, padding: '1px 4px', top: '0.6rem', right: '-0.75rem' }}
          title={`${subitemCount} subitem${subitemCount === 1 ? '' : 's'}`}
        >
          <span style={{ color: 'white', fontSize: 8, fontWeight: 700, lineHeight: 1 }}>{subitemCount}</span>
        </div>
      )}

      {/* UID badge (top-right, below the subitem-count badge) — deliberately muted
          gray, unlike the 24/5/24/7/subitem badges above: it's a reference label,
          not a status indicator, so it shouldn't compete for attention. Shown for
          every box type, unlike those other badges which only apply to 'regular'. */}
      {showUidBadge && box.uid && (
        <div
          className="absolute flex items-center justify-center pointer-events-none border-2 border-white shadow-md"
          style={{ zIndex: 21, background: '#9ca3af', borderRadius: 4, padding: '1px 5px', top: '1.7rem', right: '-0.75rem', maxWidth: 100, overflow: 'hidden' }}
          title={box.uid}
        >
          <span style={{ color: 'white', fontSize: 8, fontWeight: 700, lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{box.uid}</span>
        </div>
      )}

      {/* Completion circle */}
      {box.type === 'regular' && (editableHere || (box.completion || 0) > 0) && (
        <button
          onClick={(e) => { e.stopPropagation(); onAction('completionClick', box); }}
          className="absolute -top-3 w-6 h-6 rounded-full flex items-center justify-center pointer-events-auto bg-white border-2 border-gray-300 shadow-md overflow-hidden"
          style={{ left: '30px', zIndex: 21 }}
        >
          {(box.completion || 0) === 100 ? (
            <svg width="24" height="24" viewBox="0 0 24 24" className="absolute">
              <circle cx="12" cy="12" r="10" fill="#10b981" />
              <path d="M7 12 L10.5 15.5 L17 9" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" className="absolute">
              <circle cx="12" cy="12" r="10" fill="white" />
              {(box.completion || 0) >= 25 && <path d="M 12 12 L 12 2 A 10 10 0 0 1 22 12 Z" fill="#10b981" />}
              {(box.completion || 0) >= 50 && <path d="M 12 12 L 22 12 A 10 10 0 0 1 12 22 Z" fill="#10b981" />}
              {(box.completion || 0) >= 75 && <path d="M 12 12 L 12 22 A 10 10 0 0 1 2 12 Z" fill="#10b981" />}
            </svg>
          )}
        </button>
      )}

      {/* Start date badge (bottom-left, below box) */}
      {box.type === 'regular' && startText && (
        box.scheduledStart || box.earliestStart ||
        (showStartDates && (editableHere || box.duration))
      ) && (
        <div
          onClick={(e) => { e.stopPropagation(); onAction('startBadgeClick', box); }}
          className="absolute flex items-center gap-0.5 pointer-events-auto whitespace-nowrap cursor-pointer"
          style={{ top: 'calc(100% + 2px)', left: '2px', zIndex: 23 }}
          title={box.scheduledStart ? `Set date: ${box.scheduledStart}` : box.earliestStart ? `Earliest start: ${box.earliestStart}` : undefined}
        >
          {box.scheduledStart ? (
            <>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="#d97706">
                <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm1 11H7v-2h6V7l5 5-5 5v-4z"/>
              </svg>
              <span className="text-xs rounded px-1" style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#d97706' }}>
                {startText}
              </span>
            </>
          ) : box.earliestStart ? (
            <>
              <Calendar size={10} className="text-blue-600" />
              <span className="text-xs bg-blue-50 border border-blue-200 rounded px-1 text-blue-600">
                {startText}
              </span>
            </>
          ) : (
            <span className="text-xs bg-gray-100 border border-gray-200 rounded px-1 text-gray-500">
              {startText}
            </span>
          )}
        </div>
      )}

      {/* Finish date badge (bottom-right, below box) */}
      {box.type === 'regular' && showFinishDates && (editableHere || box.duration) && (
        <div
          onClick={(e) => { e.stopPropagation(); onAction('finishBadgeClick', box); }}
          className={`absolute text-xs px-1 py-0 rounded whitespace-nowrap pointer-events-auto cursor-pointer ${
            box.importantFinish ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-600'
          }`}
          style={{
            top: 'calc(100% + 2px)', right: '-3px', zIndex: 23,
            outline: isConflict ? '2.5px solid #ef4444' : undefined,
            outlineOffset: '1px',
          }}
        >
          {endText}
        </div>
      )}

      {/* Connection handles */}
      {showConnectHandles && (
        <>
          {(['left', 'right', 'top', 'bottom'] as const).map(side => {
            const style: React.CSSProperties = {
              position: 'absolute',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              cursor: 'pointer',
              zIndex: 20,
              ...(side === 'left'   ? { top: '50%', left: '-10px',   transform: 'translateY(-50%)' } :
                  side === 'right'  ? { top: '50%', right: '-10px',  transform: 'translateY(-50%)' } :
                  side === 'top'    ? { top: '-10px', left: '50%',   transform: 'translateX(-50%)' } :
                                      { bottom: '-10px', left: '50%', transform: 'translateX(-50%)' }),
            };
            const color = box.type === 'callout' ? 'bg-yellow-500' : 'bg-blue-500';
            const handle = (e: React.MouseEvent | React.TouchEvent) => {
              e.stopPropagation();
              onAction('connectHandle', box, side);
            };
            return (
              <div
                key={side}
                className={`absolute rounded-full cursor-pointer ${color}`}
                style={style}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={handle}
                onTouchEnd={handle}
              />
            );
          })}
        </>
      )}
    </div>
  );
});

export default CanvasBox;
